import React, { useState } from 'react';

import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { useCheckItemBuilder } from 'Src/feature/check-item/useCheckItemBuilder';
import { UsedCurrentCheckItemButtons } from 'Src/feature/check-item/useCurrentCheckItemButtons';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useGlobalSearch } from 'Src/feature/global-search/useGlobalSearch';
import { MenuScreenButton } from 'Src/feature/menu/menuScreenService';
import { useMenuItemButtons } from 'Src/feature/menu/useMenuItemButtons';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';

const sectionId = 'useMenuControl';

interface MenuControl {
  menuItemButtons: ReturnType<typeof useMenuItemButtons>;
  showMenuGroupButtons: boolean;
  isViewOnly?: (button: MenuScreenButton) => boolean;
}

interface UseMenuControlOptions {
  checkItemButtons: UsedCurrentCheckItemButtons;
}

const useMenuControl = (options: UseMenuControlOptions) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const currentOperator = useCurrentOperator();
  const { checkItemButtons } = options;

  const globalSearch = useGlobalSearch();
  const currentCheck = useCurrentCheck();
  const checkItemBuilder = useCheckItemBuilder();
  const showMenuGroupButtons = !globalSearch.value.length;

  const checkId = currentCheck.query.data?.id;
  const canMutateCheck = currentCheck.query.data?.status === 'Opened' || 'Scheduled';

  const [selectedButton, setSelectedButton] = useState<MenuScreenButton | undefined>(undefined);

  const modal = {
    openPrice: {
      ...useModalControl(),
      onDone: async (options: { button: MenuScreenButton }) => {
        const { button } = options;
        try {
          if (!canMutateCheck) return;
          await checkItemBuilder.onMenuItemClick({ checkId, checkItemButtons, menuScreenButton: button });
          setSelectedButton(undefined);
        } catch (e) {
          log.warn('modal.openPrice.onDone()', e);
        }
      }
    },
  };

  const onMenuItemClick = async (menuScreenButton: MenuScreenButton) => {
    try {
      if (!canMutateCheck) return;
      if (menuScreenButton.pricingType === 'Open') {
        setSelectedButton(menuScreenButton);
        modal.openPrice.open();
      } else {
        await checkItemBuilder.onMenuItemClick({ checkId: checkId, checkItemButtons, menuScreenButton });
      }
    } catch (e) {
      log.warn('onMenuItemClick()', e);
    }
  };

  const isViewOnly = (button: MenuScreenButton) => {
    if (!button.stock) {
      return false;
    }

    if (button.stock.quantity != null && button.stock.quantity < 1) {
      return true;
    }

    const { isAllowed } = currentOperator.permission('pos.checks.86Items.create');
    return button.stock.is86 && !isAllowed;
  };

  const menuItemButtons = useMenuItemButtons({ onClick: onMenuItemClick });

  return {
    globalSearch,
    currentCheck,
    showMenuGroupButtons,
    menuItemButtons,
    isViewOnly,
    modal,
    selectedButton,
  };
};

type UsedMenuControl = ReturnType<typeof useMenuControl>;

export { MenuControl, useMenuControl };
export type { UsedMenuControl };
